import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { saveExplorerOnboarding } from "../../api/explorerOnboardingApi";
import { isLoggedIn } from "../../auth/authStorage";
import {
  buildExplorerLoginRedirectQuery,
  explorerSignupPath,
  persistExplorerProfileFromSession,
} from "../../explorer/explorerProfileSync";
import {
  clearExplorerPendingOnboarding,
  getExplorerEventId,
  getExplorerEventCode,
  getExplorerFlow,
  getExplorerLocalProfile,
  getExplorerPendingOnboarding,
  setExplorerPendingOnboarding,
  setExplorerStep2Persisted,
} from "../../explorer/explorerSession";
import Select from "react-select";
import { sectors } from "../MuslimInvest/Sectors";
import ExplorerWizardShell from "./ExplorerWizardShell";
import { useTranslation } from "react-i18next";

/** Stored as Explorer onboarding segment (string). */
const DESCRIBES_YOU_OPTIONS = [
  {
    value: "18_23_early_entrepreneur_sme",
    label: "18–23 Years – early entrepreneur / SME",
  },
  {
    value: "23_plus_early_entrepreneur_sme",
    label: "23+ Years – early entrepreneur / SME",
  },
  {
    value: "23_plus_established_sme_1million_plus_turnover",
    label: "23+ Years established SME / £1million+ turnover",
  },
] as const;

const OBJECTIVES_FOR_ATTENDING_KEY = "objectivesForAttending";
const STAGE_OF_BUSINESS_KEY = "stageOfBusiness";
const SECTORS_OF_INTEREST_KEY = "sectorsOfInterest";
const PHOTO_SHARING_CONSENT_KEY = "photoSharingConsent";
const TERMS_ATTACHMENT_CONSENT_KEY = "termsAttachmentConsent";

const EVENT_TERMS_INTRO =
  "Event registration policy for the Support Muslim Business Forum Ltd. (SMBF). This policy applies to all events hosted by SMBF and is designed to ensure a smooth and professional experience for all participants. Please read the following terms carefully before registering for any of our events.";

const EVENT_TERMS_SECTIONS: { title: string; body: string }[] = [
  {
    title: "1. Event Registration",
    body:
      "Registrations for our events can be completed through our official websites, smbf.global or smbf.org.uk. Upon successful registration and payment, you will receive a confirmation email containing your event details. Please ensure all information provided during registration is accurate, as registrations are non-transferable.",
  },
  {
    title: "2. Payment Methods",
    body:
      "We accept various payment methods, including major credit and debit cards. All transactions are processed securely to protect your personal information.",
  },
  {
    title: "3. Non-Refundable Policy",
    body:
      "Please note that all registrations are final. Payments are non-refundable under any circumstances. This policy is in place to cover the costs associated with event planning and preparation.",
  },
  {
    title: "4. Event Cancellation or Rescheduling",
    body:
      "In the unlikely event that an SMBF event is cancelled or rescheduled, you will be notified as soon as possible. If the event is cancelled, a full refund will be issued. If the event is rescheduled, your registration will be valid for the new date. Should you be unable to attend the rescheduled date, please contact us for further assistance.",
  },
  {
    title: "5. Registration Transfer",
    body:
      "Registrations are non-transferable. Each registration is valid only for the individual whose name is on the registration form. Please bring a valid photo ID to the event for verification purposes.",
  },
  {
    title: "6. Entry Requirements",
    body:
      "We may ask for a photo ID at the event during registration that matches the name and address you used to book your registration.",
  },
  {
    title: "7. Code of Conduct",
    body:
      "All attendees are expected to behave in a professional and respectful manner. Any form of harassment or disruptive behaviour will not be tolerated and may result in removal from the event without a refund.",
  },
  {
    title: "8. Liability",
    body:
      "The organisers are not responsible for any personal injury, loss, or damage to personal property during the event. Attendees participate at their own risk.",
  },
  {
    title: "9. Contact Information",
    body:
      "For any questions or concerns regarding registrations or the event, please contact our support team at enquiries@smbf.global or 0121 357 6786.",
  },
];

const EVENT_TERMS_CLOSING =
  "By registering for an SMBF event, you agree to comply with all terms and conditions outlined in this policy. Thank you for your cooperation and understanding. We look forward to seeing you at the event!";

const OBJECTIVES_FOR_ATTENDING_OPTIONS = [
  { value: "b2b_lead_generation", label: "B2B lead generation" },
  { value: "finding_reliable_b2b_services", label: "Finding reliable B2B services" },
  { value: "getting_mentorship_advice", label: "Getting mentorship/advice" },
  { value: "supporting_muslim_businesses", label: "Supporting Muslim businesses" },
  {
    value: "finding_investment_opportunities",
    label: "Finding investment opportunities",
  },
  { value: "meeting_investors", label: "Meeting investors" },
  {
    value: "meeting_online_connections_offline",
    label: "Meeting online connections offline",
  },
  { value: "learning_more_about_smbf", label: "Learning more about SMBF" },
  { value: "general_networking", label: "General networking" },
  { value: "exploring_partnerships", label: "Exploring partnerships" },
  { value: "other", label: "Other" },
] as const;

const STAGE_OF_BUSINESS_OPTIONS = [
  { value: "Early Stage", label: "Early Stage" },
  { value: "Growth", label: "Growth" },
  { value: "Established", label: "Established" },
  { value: "Executive", label: "Executive" },
] as const;

const SECTOR_OPTIONS = [...sectors].sort().map((s) => ({ value: s, label: s }));

function parseStoredList(value?: string): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function ExplorerSegmentation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pendingInitial = useMemo(() => getExplorerPendingOnboarding(), []);
  const [segment, setSegment] = useState(() => pendingInitial?.segment ?? "");
  const [objectivesForAttending, setObjectivesForAttending] = useState(
    () => pendingInitial?.discoveryAnswers?.[OBJECTIVES_FOR_ATTENDING_KEY] ?? "",
  );
  const [stageOfBusiness, setStageOfBusiness] = useState(
    () => pendingInitial?.discoveryAnswers?.[STAGE_OF_BUSINESS_KEY] ?? "",
  );
  const [sectorsOfInterest, setSectorsOfInterest] = useState<string[]>(() =>
    parseStoredList(pendingInitial?.discoveryAnswers?.[SECTORS_OF_INTEREST_KEY]),
  );
  const [photoSharingConsent, setPhotoSharingConsent] = useState(
    () => pendingInitial?.discoveryAnswers?.[PHOTO_SHARING_CONSENT_KEY] === "true",
  );
  const [termsAttachmentConsent, setTermsAttachmentConsent] = useState(
    () => pendingInitial?.discoveryAnswers?.[TERMS_ATTACHMENT_CONSENT_KEY] === "true",
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showFieldErrors, setShowFieldErrors] = useState(false);

  const fieldInvalid = useMemo(() => {
    if (!showFieldErrors) {
      return {
        segment: false,
        objectives: false,
        stage: false,
        photo: false,
        terms: false,
      };
    }
    return {
      segment: !segment,
      objectives: !objectivesForAttending,
      stage: !stageOfBusiness,
      photo: !photoSharingConsent,
      terms: !termsAttachmentConsent,
    };
  }, [
    showFieldErrors,
    segment,
    objectivesForAttending,
    stageOfBusiness,
    photoSharingConsent,
    termsAttachmentConsent,
  ]);

  useEffect(() => {
    setError(null);
  }, [
    segment,
    objectivesForAttending,
    stageOfBusiness,
    photoSharingConsent,
    termsAttachmentConsent,
  ]);

  const authenticated = isLoggedIn();
  const explorerEventCode = useMemo(() => getExplorerEventCode(), []);

  useEffect(() => {
    const local = getExplorerLocalProfile();
    if (!local || !local.firstName?.trim() || !local.lastName?.trim()) {
      navigate(explorerSignupPath(), { replace: true });
    }
  }, [navigate]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const missingFields =
      !segment ||
      !objectivesForAttending ||
      !stageOfBusiness ||
      !photoSharingConsent ||
      !termsAttachmentConsent;

    if (missingFields) {
      setShowFieldErrors(true);
      setError(t("explorer.segmentation.completeRequired"));
      return;
    }
    setShowFieldErrors(false);
    setError(null);

    const discoveryAnswers = {
      [OBJECTIVES_FOR_ATTENDING_KEY]: objectivesForAttending,
      [STAGE_OF_BUSINESS_KEY]: stageOfBusiness,
      [SECTORS_OF_INTEREST_KEY]: sectorsOfInterest.join(", "),
      [PHOTO_SHARING_CONSENT_KEY]: String(photoSharingConsent),
      [TERMS_ATTACHMENT_CONSENT_KEY]: String(termsAttachmentConsent),
    };
    const existing = getExplorerPendingOnboarding();
    const mergedPending = {
      ...existing,
      segment,
      discoveryAnswers: {
        ...existing?.discoveryAnswers,
        ...discoveryAnswers,
      },
    };
    setExplorerPendingOnboarding(mergedPending);

    const local = getExplorerLocalProfile();
    const flow = getExplorerFlow();
    const eventIdStr = getExplorerEventId();
    const eventIdNum = eventIdStr ? parseInt(eventIdStr, 10) : NaN;
    const safeEventId = Number.isFinite(eventIdNum) ? eventIdNum : undefined;

    if (!local && !isLoggedIn()) {
      setShowFieldErrors(false);
      setError(t("explorer.segmentation.profileMissing"));
      return;
    }

    if (!local && isLoggedIn()) {
      setShowFieldErrors(false);
      setError(t("explorer.segmentation.profileMissing"));
      navigate(
        explorerEventCode
          ? `/explorer/signup/${encodeURIComponent(explorerEventCode)}`
          : "/explorer/signup",
        { replace: true },
      );
      return;
    }

    if (!local?.firstName?.trim() || !local?.lastName?.trim()) {
      setShowFieldErrors(false);
      setError(t("explorer.signup.firstLastNameRequired"));
      navigate(
        explorerEventCode
          ? `/explorer/signup/${encodeURIComponent(explorerEventCode)}`
          : "/explorer/signup",
        { replace: true },
      );
      return;
    }

    setLoading(true);
    try {
      if (local) {
        try {
          await persistExplorerProfileFromSession({
            segment,
            objectives: objectivesForAttending,
            discoveryAnswers: mergedPending.discoveryAnswers,
          });
        } catch (err: unknown) {
          const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
          if (code === "already_registered" && !isLoggedIn()) {
            navigate(`/?${buildExplorerLoginRedirectQuery(local, "/explorer/segmentation", "login")}`, {
              replace: true,
            });
            return;
          }
          throw err;
        }
      }

      if (isLoggedIn()) {
        await saveExplorerOnboarding({
          segment,
          flowType: flow,
          discoveryAnswers: mergedPending.discoveryAnswers,
          eventId: safeEventId,
        });
        clearExplorerPendingOnboarding();
      }

      setExplorerStep2Persisted();
      navigate("/explorer/checkout", { replace: true });
    } catch (err: unknown) {
      const apiCode = (err as { response?: { data?: { code?: string; error?: unknown } } })
        ?.response?.data?.code;
      if (apiCode === "profile_required" || apiCode === "profile_incomplete") {
        setError(t("explorer.segmentation.profileMissing"));
        navigate(explorerSignupPath(), { replace: true });
        return;
      }
      if (apiCode === "segmentation_incomplete") {
        setShowFieldErrors(true);
        setError(t("explorer.segmentation.completeRequired"));
        return;
      }
      let message = t("explorer.segmentation.saveFailed");
      if (err && typeof err === "object" && "response" in err) {
        const data = (err as { response?: { data?: { error?: unknown } } }).response?.data;
        if (data?.error && typeof data.error === "string") message = data.error;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ExplorerWizardShell
      step={2}
      totalSteps={3}
      progressLabel={t("explorer.segmentation.roleQuestion")}
      nextFormId="explorer-segmentation-form"
      nextLabel={t("auth.continue")}
      loading={loading}
      nextDisabled={loading}
      onBack={() =>
        navigate(
          authenticated
            ? "/dashboard"
            : explorerEventCode
              ? `/explorer/signup/${encodeURIComponent(explorerEventCode)}`
              : "/",
          {
            replace: true,
          },
        )
      }
      error={error}
    >
      <form id="explorer-segmentation-form" onSubmit={onSubmit}>
        <h1 className="explorer-wizard-title">Step 2: {t("explorer.segmentation.quickQuestion")}</h1>
        <p className="explorer-wizard-subtitle mb-0">
          {t("explorer.segmentation.subtitle")}
        </p>
        <div className="explorer-wizard-form-stack mt-3">
          <div className="explorer-wizard-form-panel mb-0">
            <div className="explorer-wizard-section-heading">{t("explorer.segmentation.aboutYou")}</div>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <label
                  className="form-label small fw-semibold text-secondary mb-2"
                  htmlFor="explorer-describes-you"
                >
                  {t("explorer.segmentation.whichDescribesYou")}
                </label>
                <select
                  id="explorer-describes-you"
                  className={`form-select${fieldInvalid.segment ? " is-invalid" : ""}`}
                  value={segment}
                  onChange={(e) => setSegment(e.target.value)}
                  aria-label={t("explorer.segmentation.whichDescribesYou")}
                  aria-invalid={fieldInvalid.segment}
                >
                  <option value="">{t("explorer.segmentation.selectOption")}</option>
                  {DESCRIBES_YOU_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-md-6">
                <label
                  className="form-label small fw-semibold text-secondary mb-2"
                  htmlFor="explorer-stage-of-business"
                >
                  {t("events.stageOfBusiness")}
                </label>
                <select
                  id="explorer-stage-of-business"
                  className={`form-select${fieldInvalid.stage ? " is-invalid" : ""}`}
                  value={stageOfBusiness}
                  onChange={(e) => setStageOfBusiness(e.target.value)}
                  aria-label={t("events.stageOfBusiness")}
                  aria-invalid={fieldInvalid.stage}
                >
                  <option value="">{t("explorer.segmentation.select")}</option>
                  {STAGE_OF_BUSINESS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-md-6">
                <label
                  className="form-label small fw-semibold text-secondary mb-2"
                  htmlFor="explorer-objectives-for-attending"
                >
                  {t("explorer.segmentation.objectivesForAttending")}
                </label>
                <select
                  id="explorer-objectives-for-attending"
                  className={`form-select${fieldInvalid.objectives ? " is-invalid" : ""}`}
                  value={objectivesForAttending}
                  onChange={(e) => setObjectivesForAttending(e.target.value)}
                  aria-label={t("explorer.segmentation.objectivesForAttending")}
                  aria-invalid={fieldInvalid.objectives}
                >
                  <option value="">{t("explorer.segmentation.selectObjective")}</option>
                  {OBJECTIVES_FOR_ATTENDING_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="explorer-wizard-form-panel mb-0">
            <div className="explorer-wizard-section-heading">{t("explorer.segmentation.preferences")}</div>
            <label
              className="form-label small fw-semibold text-secondary mb-2"
              htmlFor="explorer-sectors-of-interest"
            >
              {t("events.sectorsOfInterest")}{" "}
              <span className="fw-normal text-muted">({t("explorer.segmentation.optional")})</span>
            </label>
            <div className="explorer-wizard-react-select">
              <Select
                inputId="explorer-sectors-of-interest"
                options={SECTOR_OPTIONS}
                isMulti
                isClearable
                placeholder={t("explorer.segmentation.selectSectors")}
                value={sectorsOfInterest.map((val) => ({ value: val, label: val }))}
                onChange={(selected) => {
                  const values = (selected as { value: string; label: string }[] | null)?.map(
                    (opt) => opt.value,
                  );
                  setSectorsOfInterest(values ?? []);
                }}
                classNamePrefix="react-select"
                aria-label={t("explorer.segmentation.sectorsOptionalAria")}
              />
            </div>
          </div>

          <div className="explorer-wizard-form-panel mb-0">
            <div className="explorer-wizard-section-heading">{t("explorer.segmentation.termsConsent")}</div>
            <p className="small text-secondary mb-3 mb-md-2">
              {t("explorer.segmentation.reviewTerms")}
            </p>
            <div
              className="explorer-wizard-terms-preview"
              role="region"
              aria-label={t("explorer.segmentation.termsAndConditions")}
              tabIndex={0}
            >
              <div className="explorer-wizard-terms-content">
                <p className="fw-bold mb-1 text-body">{t("footer.eventPolicy")}</p>
                <p className="fw-bold mb-3 text-body">{t("explorer.segmentation.effectiveDate")}</p>
                <p className="mb-3 mb-0">{EVENT_TERMS_INTRO}</p>
                {EVENT_TERMS_SECTIONS.map((section) => (
                  <div key={section.title} className="mb-3">
                    <p className="fw-bold mb-1 mb-0 text-body">{section.title}</p>
                    <p className="mb-0">{section.body}</p>
                  </div>
                ))}
                <p className="mb-3 mb-0">{EVENT_TERMS_CLOSING}</p>
                <p className="fw-bold mb-1 mb-0 text-body">{t("explorer.segmentation.smbfLtd")}</p>
                <p className="fw-bold mb-0 text-body">{t("explorer.segmentation.effectiveDate")}</p>
              </div>
            </div>

            <hr className="explorer-wizard-consent-divider" />

            <div
              className={`explorer-wizard-consent-row form-check mb-3${
                fieldInvalid.photo ? " explorer-wizard-consent-row--invalid" : ""
              }`}
            >
              <input
                id="explorer-photo-consent"
                className={`form-check-input mt-1${fieldInvalid.photo ? " is-invalid" : ""}`}
                type="checkbox"
                checked={photoSharingConsent}
                onChange={(e) => setPhotoSharingConsent(e.target.checked)}
                aria-invalid={fieldInvalid.photo}
              />
              <label className="form-check-label small text-body-secondary" htmlFor="explorer-photo-consent">
                {t("explorer.segmentation.photoConsentText")}
              </label>
            </div>
            <div
              className={`explorer-wizard-consent-row form-check mb-0${
                fieldInvalid.terms ? " explorer-wizard-consent-row--invalid" : ""
              }`}
            >
              <input
                id="explorer-terms-attachment-consent"
                className={`form-check-input mt-1${fieldInvalid.terms ? " is-invalid" : ""}`}
                type="checkbox"
                checked={termsAttachmentConsent}
                onChange={(e) => setTermsAttachmentConsent(e.target.checked)}
                aria-invalid={fieldInvalid.terms}
              />
              <label
                className="form-check-label small text-body-secondary"
                htmlFor="explorer-terms-attachment-consent"
              >
                {t("explorer.segmentation.agreeTerms")}
              </label>
            </div>
          </div>
        </div>
      </form>
    </ExplorerWizardShell>
  );
}

export default ExplorerSegmentation;
